import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  LogoutOutlined,
  ConfirmationNumberOutlined,
  SettingsOutlined,
  Visibility,
  VisibilityOff,
  EventOutlined,
  GroupsOutlined,
  PersonOutlined,
  CheckCircleOutline,
  ScheduleOutlined,
} from '@mui/icons-material';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { customerTicketsService } from '../services/customerTicketsService';
import { customerAuthService } from '../services/customerAuthService';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import QRCode from 'qrcode';
import { userService } from '../../../shared/services/users/usersServices';
import { customerApiService } from '../services/customerApiService';

interface Ticket {
  id: string;
  code: string;
  qrCodeUrl?: string;
  status: string;
  value: number;
  enabled: boolean;
  checked_in: boolean;
  checked_in_at?: string;
  is_own_ticket?: boolean;
  is_group_ticket?: boolean;
  participant: {
    name: string;
    ticket_type: string;
    payment_status: string;
    is_group_principal?: boolean;
  };
  event: {
    id?: string;
    title: string;
    start_date: string;
    start_time: string;
    description: string;
    image?: string;
  };
}

interface EventTicketGroup {
  eventKey: string;
  title: string;
  startDate: string;
  startTime: string;
  image?: string;
  tickets: Ticket[];
  isGroupRegistration: boolean;
  isGroupPrincipal: boolean;
  ownTickets: Ticket[];
  groupMemberTickets: Ticket[];
}

const validationSchema = Yup.object().shape({
  newPassword: Yup.string().required('Nova senha obrigatória').min(6, 'Mínimo 6 caracteres'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'As senhas não coincidem')
    .required('Confirme a nova senha'),
});

function getTicketTypeLabel(type: string) {
  switch (type) {
    case 'inteiro': return 'Inteiro';
    case 'meia': return 'Meia';
    case 'estudante': return 'Estudante';
    case 'crianca': return 'Criança';
    default: return type;
  }
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('pt-BR');
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

function getStatusColor(ticket: Ticket) {
  if (ticket.checked_in) return 'success';
  if (ticket.enabled && ticket.status === 'paid') return 'primary';
  if (ticket.status === 'reserved') return 'warning';
  return 'default';
}

function getStatusText(ticket: Ticket) {
  if (ticket.checked_in) return 'Check-in Realizado';
  if (ticket.enabled && ticket.status === 'paid') return 'Pago - Habilitado';
  if (ticket.status === 'reserved') return 'Reservado - Aguardando Pagamento';
  return 'Cancelado';
}

function groupTicketsByEvent(tickets: Ticket[]): EventTicketGroup[] {
  const groupsMap = new Map<string, EventTicketGroup>();

  tickets.forEach((ticket) => {
    const eventKey = ticket.event.id || `${ticket.event.title}-${ticket.event.start_date}`;

    if (!groupsMap.has(eventKey)) {
      groupsMap.set(eventKey, {
        eventKey,
        title: ticket.event.title,
        startDate: ticket.event.start_date,
        startTime: ticket.event.start_time,
        image: ticket.event.image,
        tickets: [],
        isGroupRegistration: false,
        isGroupPrincipal: false,
        ownTickets: [],
        groupMemberTickets: [],
      });
    }

    groupsMap.get(eventKey)!.tickets.push(ticket);
  });

  return Array.from(groupsMap.values())
    .map((group) => {
      const ownTickets = group.tickets.filter((ticket) => ticket.is_own_ticket !== false);
      const groupMemberTickets = group.tickets.filter(
        (ticket) => ticket.is_group_ticket && ticket.is_own_ticket === false,
      );
      const isGroupRegistration = group.tickets.some((ticket) => ticket.is_group_ticket);
      const isGroupPrincipal = group.tickets.some(
        (ticket) => ticket.participant.is_group_principal,
      );

      return {
        ...group,
        ownTickets,
        groupMemberTickets,
        isGroupRegistration,
        isGroupPrincipal,
      };
    })
    .sort(
      (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
    );
}

interface TicketCardProps {
  ticket: Ticket;
  qrCodes: Record<string, string>;
}

function TicketCard({ ticket, qrCodes }: TicketCardProps) {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {ticket.participant.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getTicketTypeLabel(ticket.participant.ticket_type)} · {formatCurrency(ticket.value)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
            <Chip label={getStatusText(ticket)} color={getStatusColor(ticket)} size="small" />
            {ticket.is_group_ticket && !ticket.is_own_ticket && (
              <Chip label="Membro do grupo" size="small" color="secondary" variant="outlined" />
            )}
            {ticket.is_group_ticket && ticket.participant.is_group_principal && (
              <Chip label="Responsável" size="small" color="primary" variant="outlined" />
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Código do ingresso
          </Typography>
          <Typography variant="h6" fontFamily="monospace">
            {ticket.code}
          </Typography>
        </Box>

        {qrCodes[ticket.code] && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              QR Code do ingresso
            </Typography>
            <Box
              sx={{
                display: 'flex',
                p: 2,
                bgcolor: 'white',
                borderRadius: 1,
                mt: 1,
                border: '1px solid #ddd',
                justifyContent: 'center',
              }}
            >
              <img
                src={qrCodes[ticket.code]}
                alt={`QR Code - ${ticket.code}`}
                style={{ width: 150, height: 150, display: 'block' }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Apresente este código na entrada do evento
            </Typography>
          </Box>
        )}

        {ticket.checked_in && ticket.checked_in_at && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="success.main">
              Check-in realizado em: {new Date(ticket.checked_in_at).toLocaleString('pt-BR')}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

interface EventTicketsSectionProps {
  group: EventTicketGroup;
  qrCodes: Record<string, string>;
}

interface TicketsListProps {
  tickets: Ticket[];
  qrCodes: Record<string, string>;
  emptyTitle: string;
  emptyDescription: string;
  emptyIcon: React.ReactNode;
}

function TicketsList({
  tickets,
  qrCodes,
  emptyTitle,
  emptyDescription,
  emptyIcon,
}: TicketsListProps) {
  const eventGroups = useMemo(() => groupTicketsByEvent(tickets), [tickets]);

  if (tickets.length === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          {emptyIcon}
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
            {emptyTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {emptyDescription}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {eventGroups.length > 1 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Organizados por evento ({eventGroups.length} eventos).
        </Typography>
      )}
      {eventGroups.map((group) => (
        <EventTicketsSection key={group.eventKey} group={group} qrCodes={qrCodes} />
      ))}
    </Box>
  );
}

function EventTicketsSection({ group, qrCodes }: EventTicketsSectionProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        mb: 3,
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <EventOutlined sx={{ mt: 0.25, flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {group.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {formatDate(group.startDate)} às {group.startTime}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
              <Chip
                size="small"
                label={`${group.tickets.length} ingresso${group.tickets.length !== 1 ? 's' : ''}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'inherit' }}
              />
              {group.isGroupRegistration && (
                <Chip
                  size="small"
                  icon={<GroupsOutlined sx={{ color: 'inherit !important' }} />}
                  label="Inscrição em grupo"
                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'inherit' }}
                />
              )}
              {group.isGroupPrincipal && (
                <Chip
                  size="small"
                  label="Você é o responsável pelo pagamento"
                  sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: 'inherit', fontWeight: 600 }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {group.ownTickets.length > 0 && (
          <Box sx={{ mb: group.groupMemberTickets.length > 0 ? 3 : 0 }}>
            {group.isGroupRegistration && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonOutlined fontSize="small" color="action" />
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                    {group.ownTickets.length > 1 ? 'Seus ingressos neste evento' : 'Seu ingresso neste evento'}
                  </Typography>
                </Box>
              </>
            )}
            <Grid container spacing={2}>
              {group.ownTickets.map((ticket) => (
                <Grid item xs={12} md={6} lg={4} key={ticket.id}>
                  <TicketCard ticket={ticket} qrCodes={qrCodes} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {group.groupMemberTickets.length > 0 && (
          <Box>
            {group.ownTickets.length > 0 && <Divider sx={{ mb: 3 }} />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <GroupsOutlined fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                Ingressos dos participantes do grupo ({group.groupMemberTickets.length})
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {group.groupMemberTickets.map((ticket) => (
                <Grid item xs={12} md={6} lg={4} key={ticket.id}>
                  <TicketCard ticket={ticket} qrCodes={qrCodes} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

type TicketTab = 'pending' | 'used';

const CustomerTickets: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TicketTab>('pending');
  const { customer, logout } = useCustomerAuth();
  const navigate = useNavigate();

  // Estados para QR codes
  const [qrCodes, setQrCodes] = useState<{[key: string]: string}>({});
  
  // Estados para modal de alteração de senha
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Função para gerar QR Code
  const generateQRCode = async (ticketCode: string) => {
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(ticketCode, {
        width: 150,
        margin: 1,
      });
      setQrCodes(prev => ({ ...prev, [ticketCode]: qrCodeDataUrl }));
    } catch (error) {
      console.error('Erro ao gerar QR Code:', error);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await customerTicketsService.getMyTickets();
      setTickets(response.data.tickets || []);
    } catch (error: any) {
      setError('Erro ao carregar ingressos: ' + (error?.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/customer/login');
  };

  const handleTogglePassword = () => setShowPassword((prev) => !prev);
  const handleToggleConfirm = () => setShowConfirm((prev) => !prev);

  const handleChangePassword = async (values: any, { resetForm }: any) => {
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      // Usar o serviço que já existe
      await customerApiService.changePassword(customer?.id, values.newPassword);
      setPasswordSuccess('Senha alterada com sucesso!');
      resetForm();
      
      // Fechar modal após 2 segundos
      setTimeout(() => {
        setShowChangePassword(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (error: any) {
      setPasswordError(error?.response?.data?.message || 'Erro ao alterar senha');
    } finally {
      setPasswordLoading(false);
    }
  };

  const hasGroupTickets = tickets.some(
    (ticket) => ticket.is_group_ticket && !ticket.is_own_ticket,
  );

  const pendingTickets = useMemo(
    () => tickets.filter((ticket) => !ticket.checked_in),
    [tickets],
  );

  const usedTickets = useMemo(
    () => tickets.filter((ticket) => ticket.checked_in),
    [tickets],
  );

  // Gerar QR codes para todos os tickets
  useEffect(() => {
    tickets.forEach(ticket => {
      if (ticket.code && !qrCodes[ticket.code]) {
        generateQRCode(ticket.code);
      }
    });
  }, [tickets]);

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Typography>Carregando ingressos...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box>
      {/* Header */}
      <AppBar position="static">
        <Toolbar>
          <ConfirmationNumberOutlined sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Meus Ingressos
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              color="inherit" 
              onClick={() => setShowChangePassword(true)}
              title="Alterar Senha"
            >
              <SettingsOutlined />
            </IconButton>
            <Avatar sx={{ width: 32, height: 32 }}>
              {customer?.name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Typography variant="body2">
              {customer?.name}
            </Typography>
            <IconButton color="inherit" onClick={handleLogout}>
              <LogoutOutlined />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Container sx={{ mt: 4, mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {hasGroupTickets && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Como responsável pela inscrição em grupo, você também pode visualizar os ingressos dos demais participantes.
          </Alert>
        )}

        {tickets.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <ConfirmationNumberOutlined sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Nenhum ingresso encontrado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Você ainda não possui ingressos cadastrados.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Box>
            <Tabs
              value={activeTab}
              onChange={(_, value: TicketTab) => setActiveTab(value)}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                value="pending"
                icon={<ScheduleOutlined sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label={`Não utilizados (${pendingTickets.length})`}
                sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
              />
              <Tab
                value="used"
                icon={<CheckCircleOutline sx={{ fontSize: 20 }} />}
                iconPosition="start"
                label={`Utilizados (${usedTickets.length})`}
                sx={{ textTransform: 'none', fontWeight: 600, minHeight: 48 }}
              />
            </Tabs>

            {activeTab === 'pending' ? (
              <TicketsList
                tickets={pendingTickets}
                qrCodes={qrCodes}
                emptyTitle="Nenhum ingresso disponível"
                emptyDescription="Todos os seus ingressos já foram utilizados ou ainda não há ingressos pendentes."
                emptyIcon={
                  <ScheduleOutlined sx={{ fontSize: 64, color: 'text.secondary' }} />
                }
              />
            ) : (
              <TicketsList
                tickets={usedTickets}
                qrCodes={qrCodes}
                emptyTitle="Nenhum ingresso utilizado"
                emptyDescription="Os ingressos com check-in realizado aparecerão aqui."
                emptyIcon={
                  <CheckCircleOutline sx={{ fontSize: 64, color: 'text.secondary' }} />
                }
              />
            )}
          </Box>
        )}
      </Container>

      {/* Modal de Alterar Senha */}
      <Dialog 
        open={showChangePassword} 
        onClose={() => setShowChangePassword(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Alterar Senha</DialogTitle>
        <DialogContent>
          <Formik
            initialValues={{ newPassword: '', confirmPassword: '' }}
            validationSchema={validationSchema}
            onSubmit={handleChangePassword}
          >
            {({ errors, touched, handleChange, values }) => (
              <Form>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Field
                    as={TextField}
                    label="Nova senha"
                    name="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={values.newPassword}
                    onChange={handleChange}
                    required
                    fullWidth
                    disabled={passwordLoading}
                    error={!!(touched.newPassword && errors.newPassword)}
                    helperText={touched.newPassword && errors.newPassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleTogglePassword} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Field
                    as={TextField}
                    label="Confirme a nova senha"
                    name="confirmPassword"
                    type={showConfirm ? 'text' : 'password'}
                    value={values.confirmPassword}
                    onChange={handleChange}
                    required
                    fullWidth
                    disabled={passwordLoading}
                    error={!!(touched.confirmPassword && errors.confirmPassword)}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleToggleConfirm} edge="end">
                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {passwordSuccess && <Alert severity="success">{passwordSuccess}</Alert>}
                  {passwordError && <Alert severity="error">{passwordError}</Alert>}
                </Box>
              </Form>
            )}
          </Formik>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChangePassword(false)}>
            Cancelar
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={passwordLoading}
            onClick={() => {
              const form = document.querySelector('form');
              if (form) {
                const event = new Event('submit', { bubbles: true, cancelable: true });
                form.dispatchEvent(event);
              }
            }}
          >
            {passwordLoading ? 'Alterando...' : 'Alterar Senha'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerTickets;